// KENNYVICOPS — Core Services data (single source of truth)
import { Search, Bug, KeyRound, Network, Database, GraduationCap } from 'lucide-react';

export const SERVICES = [
  {
    slug: 'security-assessment',
    icon: Search,
    name: 'Security Assessment',
    headline: 'Know Where You Stand Before Attackers Find the Gaps.',
    short:
      'Assess your environment, identify weaknesses, evaluate existing controls and prioritize practical improvements.',
    description:
      'A structured review of your security environment to surface real risks and produce a prioritized, business-aware improvement plan.',
    problem:
      "Most small and growing businesses don't know exactly where their real security gaps are — or which ones matter most. Without that clarity, security spending becomes reactive, scattered and hard to justify.",
    whatWeDo:
      'We work with you to understand your business, your environment and your priorities — then systematically review the security controls that actually matter for how you operate. The output is a plain-language, prioritized plan you can act on.',
    areas: [
      'Security posture assessment',
      'Asset and environment review',
      'Security control review',
      'Risk identification',
      'Gap analysis',
      'Security recommendations',
      'Prioritized improvement roadmap',
    ],
    whoItHelps: [
      'Businesses that have never had a formal security review',
      'Owners who suspect gaps but can\'t point at them',
      'Teams preparing for a new client, contract or compliance requirement',
      'Companies that want a clear improvement plan — not a shopping list of tools',
    ],
    outcome:
      'You leave with a written, prioritized view of your real security risks — and a practical, business-aware plan for what to fix first, second and third.',
    videoTopics: [
      'Risk vs Threat vs Vulnerability',
      'Attack Surface',
      'Asset Inventory',
      'Security Assessment',
      'Preventive / Detective / Corrective Controls',
    ],
    image:
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80&auto=format&fit=crop',
    accent: 'from-cyan-400 to-blue-500',
    ring: 'border-cyan-500/30',
  },
  {
    slug: 'vulnerability-management',
    icon: Bug,
    name: 'Vulnerability Management',
    headline: 'Find the Weaknesses Before Attackers Exploit Them.',
    short:
      'Identify, prioritize, remediate and continuously manage vulnerabilities across your technology environment.',
    description:
      'Move beyond one-off scans to a continuous discipline of finding and fixing the weaknesses that matter most to your business.',
    problem:
      "Attackers exploit known weaknesses in software, systems and configurations every day. Businesses that don't have a repeatable way to find and fix those weaknesses are always one bad day away from a preventable incident.",
    whatWeDo:
      'We help you put a real vulnerability management lifecycle in place — Discover → Assess → Prioritize → Remediate → Validate → Monitor — so vulnerabilities become a managed process instead of a recurring surprise.',
    areas: [
      'Asset discovery',
      'Vulnerability discovery and scanning',
      'Risk prioritization',
      'Remediation tracking',
      'Patch management support',
      'Validation of fixes',
      'Continuous monitoring',
      'Reporting for leadership',
    ],
    whoItHelps: [
      'Businesses running scans but unsure how to act on the results',
      'Organizations without a defined patching or remediation process',
      'Teams that need reporting they can share with leadership',
      'Companies exposed to third-party or internet-facing systems',
    ],
    outcome:
      'You gain a repeatable discipline that turns vulnerabilities from a source of anxiety into a managed, measurable process — with clear ownership and evidence you can point to.',
    videoTopics: [
      'Vulnerability Management',
      'Attack Surface',
      'Real-world vulnerability incidents',
      'Equifax case',
      'Patch and vulnerability concepts',
    ],
    image:
      'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1200&q=80&auto=format&fit=crop',
    accent: 'from-blue-400 to-indigo-500',
    ring: 'border-blue-500/30',
  },
  {
    slug: 'identity-access-security',
    icon: KeyRound,
    name: 'Identity & Access Security',
    headline: 'The Right People. The Right Access. At the Right Time.',
    short:
      'Strengthen identity and access controls so users have appropriate access to the resources they need — and nothing more.',
    description:
      'Build discipline into who has access to what, why they have it, and how that access is granted, reviewed and removed.',
    problem:
      'Most modern breaches start with an identity — a stolen password, an over-privileged account, a former employee whose access was never removed. Access sprawl and weak identity controls quietly become a business\'s biggest security risk.',
    whatWeDo:
      'We help you get identity and access right — from MFA on the systems that matter, to least privilege on day-to-day accounts, to real controls around privileged and administrative access — and a repeatable joiner/mover/leaver process.',
    areas: [
      'Identity management',
      'Access management',
      'Multi-Factor Authentication (MFA)',
      'Least privilege',
      'Privileged Access Management (PAM)',
      'Role-based access',
      'Access reviews',
      'Account lifecycle management',
      'Joiner / Mover / Leaver processes',
      'Authentication and authorization',
    ],
    whoItHelps: [
      'Businesses without MFA on critical systems',
      'Teams unsure who has access to what',
      'Companies with shared or privileged accounts',
      'Organizations where offboarding is inconsistent',
    ],
    outcome:
      'The right people have the right access at the right time — with MFA where it matters, privileged accounts under control, and a clear picture of who can reach what.',
    videoTopics: [
      'Least Privilege',
      'MFA',
      'Authentication vs Authorization',
      'PAM',
      'Insider Threat',
      'Former Employee Access',
    ],
    image:
      'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=1200&q=80&auto=format&fit=crop',
    accent: 'from-sky-400 to-cyan-500',
    ring: 'border-sky-500/30',
  },
  {
    slug: 'endpoint-network-security',
    icon: Network,
    name: 'Endpoint & Network Security',
    headline: 'Protect Your Devices. Secure Your Network.',
    short:
      'Strengthen endpoints, networks and security controls to reduce exposure and improve detection and response.',
    description:
      'Harden the devices and networks your business runs on, and put the visibility in place to spot and stop threats early.',
    problem:
      "Laptops, phones, servers and networks are where most attacks actually land. Weak endpoint protection, flat networks and blind spots in monitoring turn small incidents into business-wide problems.",
    whatWeDo:
      'We help you strengthen the endpoints and networks your business runs on — with modern endpoint protection, sensible network segmentation, secure remote access, and the logging and monitoring you need to catch problems early.',
    areas: [
      'Endpoint protection',
      'Endpoint Detection & Response (EDR)',
      'Device security',
      'Network security and visibility',
      'Network segmentation',
      'Firewall controls',
      'Secure remote access',
      'Logging and monitoring',
      'Detection and response',
    ],
    whoItHelps: [
      'Businesses relying only on traditional antivirus',
      'Companies with remote or hybrid workforces',
      'Organizations without visibility into what\'s happening on their network',
      'Teams that need faster detection and response',
    ],
    outcome:
      'Your devices are protected, your network is segmented and visible, and you have the logs and monitoring to catch trouble early — not weeks later.',
    videoTopics: [
      'Endpoint Security',
      'EDR',
      'Firewall',
      'Network Segmentation',
      'VPN',
      'Logging and Monitoring',
    ],
    image:
      'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200&q=80&auto=format&fit=crop',
    accent: 'from-indigo-400 to-blue-500',
    ring: 'border-indigo-500/30',
  },
  {
    slug: 'data-protection-backup',
    icon: Database,
    name: 'Data Protection & Backup',
    headline: 'Protect Your Data. Prepare to Recover.',
    short:
      'Protect critical information, control access, maintain reliable backups and improve recovery readiness.',
    description:
      'Understand where your critical data lives, protect it with the right controls, and be ready to recover when something goes wrong.',
    problem:
      "Your data is the reason attackers show up. If you don't know where your critical data lives, who can touch it, or whether your backups can actually restore it — you're gambling with the survival of the business.",
    whatWeDo:
      'We help you identify and classify your critical data, protect it with the right controls, and — most importantly — put backup and recovery practices in place that you\'ve actually tested.',
    areas: [
      'Data identification and classification',
      'Access control',
      'Data Loss Prevention (DLP)',
      'Encryption at rest and in transit',
      'Backup strategy',
      'Recovery planning',
      'Backup protection',
      'Recovery testing',
      'Ransomware recovery readiness',
    ],
    whoItHelps: [
      'Businesses holding sensitive customer, financial or health data',
      'Companies with untested or ad-hoc backups',
      'Organizations concerned about ransomware',
      'Teams working toward compliance requirements',
    ],
    outcome:
      'You know where your critical data lives, it\'s protected by the right controls, and — most importantly — you have tested confidence that you could recover from a bad day.',
    videoTopics: [
      'Encryption',
      'Encryption at Rest vs In Transit',
      'Data Loss Prevention',
      'Data Backup',
      'Ransomware',
      'Data Classification',
      'Change Healthcare incident',
    ],
    image:
      'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=1200&q=80&auto=format&fit=crop',
    accent: 'from-cyan-400 to-teal-500',
    ring: 'border-cyan-500/30',
  },
  {
    slug: 'security-awareness-training',
    icon: GraduationCap,
    name: 'Security Awareness & Training',
    headline: 'A More Aware Team. A More Secure Business.',
    short:
      'Help employees recognize phishing, social engineering, suspicious requests and other common cyber threats.',
    description:
      'Turn your team into a genuine layer of defense with training that is practical, memorable and grounded in real-world examples.',
    problem:
      "Attackers don't just target technology — they target the people who use it. Phishing, business email compromise and social engineering succeed because employees haven't been given the training or context to recognize them.",
    whatWeDo:
      'We deliver practical, real-world security awareness training that helps employees recognize suspicious activity, question unusual requests, and know exactly how to report concerns — without blaming them for being human.',
    areas: [
      'Security awareness',
      'Phishing awareness',
      'Social engineering',
      'Business Email Compromise (BEC)',
      'Suspicious links and attachments',
      'Credential protection',
      'MFA awareness',
      'Reporting suspicious activity',
      'Employee security training',
      'Security culture',
    ],
    whoItHelps: [
      'Businesses that have never delivered structured security training',
      'Teams that faced a phishing incident and want to prevent the next one',
      'Companies with new hires or growing workforces',
      'Organizations working toward a security-aware culture',
    ],
    outcome:
      'Your team becomes a genuine layer of defense — able to recognize suspicious activity, question unusual requests, and report concerns before they turn into incidents.',
    videoTopics: [
      'Phishing',
      'Social Engineering',
      'Business Email Compromise',
      'Suspicious Links',
      'Security Awareness',
      'Human Risk',
    ],
    image:
      'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&q=80&auto=format&fit=crop',
    accent: 'from-blue-400 to-sky-500',
    ring: 'border-blue-500/30',
  },
];

export const getServiceBySlug = (slug) => SERVICES.find((s) => s.slug === slug);
